import { Component, inject, OnDestroy, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { SearchAccountParameters } from '@api-proxies';
import { AccountService } from '@http/clients/account.service';
import { FilterStateService } from '@services/filter-state.service';
import { GridService } from '@services/grid.service';
import { CellSorting, ServerSideGridComponent } from '@shared/grids/server-side-grid/server-side-grid.component';
import { normalize, onlySortingChanged } from '@utils/filter-list-compare';
import { ColDef, IServerSideDatasource } from 'ag-grid-enterprise';
import { Subscription } from 'rxjs';
import { ACCOUNTS_FILTER_KEY, IFilterValueAccounts } from '../accounts-filters/accounts-filter.config';
import { ACCOUNTS_GRID_COLUMNS } from './accounts-list.config';

const accountsFiltersSpec = {
    searchText: normalize.primitive,
    accountType: normalize.id,
    market: normalize.id,
    salespersons: normalize.arrayOf(normalize.externalId),
    includeArchived: normalize.boolean
};

@Component({
    selector: 'app-accounts-list',
    standalone: true,
    templateUrl: './accounts-list.component.html',
    styleUrls: ['./accounts-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ServerSideGridComponent]
})
export class AccountsListComponent implements OnInit, OnDestroy {
    filtersKey = ACCOUNTS_FILTER_KEY;

    filters?: IFilterValueAccounts;

    accountsDatasource?: IServerSideDatasource;

    appliedSort?: { sortBy: string; sortDirection: string } | undefined;

    gridColumns: ColDef[] = [...ACCOUNTS_GRID_COLUMNS];

    noResults = signal<boolean>(false);

    private filterSubscription?: Subscription;
    private isCreatingDatasource = false;

    private readonly accountService = inject(AccountService);
    private readonly filterStateService = inject(FilterStateService<IFilterValueAccounts>);
    private readonly gridService = inject(GridService);

    async ngOnInit(): Promise<void> {
        if (this.filtersKey) {
            const filtersFromLocalStorage = this.filterStateService.getFromLocalStorage(this.filtersKey);
            this.filters = filtersFromLocalStorage;
        }

        // Wait for initial filters (including URL params) before creating datasource
        this.filterSubscription = this.filterStateService.stateFilters$.subscribe(async (newFilters) => {
            if (!newFilters) return;

            // Prevent race conditions
            if (this.isCreatingDatasource) return;

            // Keep reference to old filters to avoid double API calls on sorting
            // AG-Grid handles sorting internally, but we need to sync with URL parameters
            const oldFilters = this.filters;

            this.filters = { ...newFilters };

            const datasourceExists = this.accountsDatasource && Object.keys(this.accountsDatasource).length;
            if (!datasourceExists) {
                // Apply initial sorting from URL only once (on first datasource creation)
                this.updateSort();

                this.isCreatingDatasource = true;
                await this.createDatasource();
            } else if (!onlySortingChanged(oldFilters, newFilters, accountsFiltersSpec)) {
                this.refreshGridView();
            }
        });
    }

    ngOnDestroy(): void {
        if (this.filterSubscription) {
            this.filterSubscription.unsubscribe();
        }
        // Reset race condition flag on cleanup
        this.isCreatingDatasource = false;
    }

    async onSortChanged(event: CellSorting | null) {
        this.filterStateService.onOrderChanged({
            displayName: event?.displayName,
            order: event?.sortBy
        });
    }

    async refreshGridView() {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        gridApi.refreshServerSide({ purge: true });
    }

    private searchParamsBuilder(gridParams: any, filters: IFilterValueAccounts): SearchAccountParameters {
        const { startRow, sortModel } = gridParams;
        const pageSize = 20;
        const pageNumber = Math.floor(startRow / pageSize) + 1;

        const searchParams = {
            pageNumber,
            pageSize,

            ...(sortModel?.length && sortModel[0].sort
                ? {
                      sortBy: sortModel[0].colId,
                      sortDirection: sortModel[0].sort === 'asc' ? 'A' : 'D'
                  }
                : {}),
            filterText: filters?.searchText ?? undefined,
            accountTypeId: filters?.accountType?.id,
            marketId: filters?.market?.id,
            salespersons: filters?.salespersons?.length
                ? filters?.salespersons.map((s: any) => s.externalId)
                : undefined,
            includeArchived: filters?.includeArchived ?? undefined
        } as SearchAccountParameters;

        return searchParams;
    }

    private async createDatasource(): Promise<void> {
        this.accountsDatasource = {
            getRows: async (params: any) => {
                try {
                    this.noResults.set(false);
                    const searchParams = this.searchParamsBuilder(
                        params.request,
                        this.filters ?? ({} as IFilterValueAccounts)
                    );

                    const response = await this.accountService.searchAccount(searchParams);
                    this.noResults.set(response.results?.length === 0);

                    if (response && response.results) {
                        const rowData = response.results || [];
                        const totalCount = response.pageInfo?.itemsCount || 0;

                        params.success({
                            rowData: rowData,
                            rowCount: totalCount
                        });
                    } else {
                        console.error('Invalid response structure:', response);
                        params.fail();
                    }
                } catch (error) {
                    console.error('Error fetching data:', error);
                    params.fail();
                    this.noResults.set(true);
                }
            }
        };

        // Reset race condition flag after datasource is created
        this.isCreatingDatasource = false;
    }

    private updateSort(): void {
        if (this.filters?.sortBy && this.filters?.sortDirection) {
            this.appliedSort = {
                sortBy: this.filters.sortBy,
                sortDirection: this.filters.sortDirection
            };
        } else {
            this.appliedSort = undefined;
        }
    }
}
